import axios from 'axios';
import { generateRandomString, isNullOrUndefined } from '../helpers/helpers';
import { IMPORT_TYPES } from './massImport.constants';
import {
  applyCustomFieldsForPayload,
  getBoardIdsFromCustomFieldSections,
  getCustomFieldHeaders,
} from './customFieldImportHelpers';
import {
  applyBucketImportCustomerLink,
  applyBucketImportProjectLink,
} from './bucketImportHelpers';
import { buildUpdateUserImportPayload } from './bulkUserUpdateImportHelpers';
import { buildUpdateMaterialImportPayload } from './bulkMaterialUpdateImportHelpers';
import { buildUpdateEquipmentImportPayload } from './bulkEquipmentUpdateImportHelpers';
import { validateCostCodeConflict } from './costCodeImportHelpers';

export {
  getBoardIdsFromCustomFieldSections,
  getCustomFieldHeaders,
} from './customFieldImportHelpers';

/**
 * Derives a username from a full name in the format first.last
 * Strips non-alphanumeric characters and lowercases
 * @param {string} name - Full name (e.g. "John Smith")
 * @returns {string} - Username (e.g. "john.smith")
 */
const deriveUsername = (name = '') => {
  const parts = name.trim().split(/\s+/);
  const first = (parts[0] || '').replaceAll(/[^a-zA-Z0-9]/g, '').toLowerCase();
  const last = (parts[parts.length - 1] || '').replaceAll(/[^a-zA-Z0-9]/g, '').toLowerCase();
  if (!first) return '';
  return parts.length > 1 ? `${first}.${last}` : first;
};

/**
 * Transforms a row from the spreadsheet using the header mapping
 * @param {Object} row - Raw row from spreadsheet
 * @param {Object} headerMapping - Mapping of field keys to spreadsheet column names
 * @returns {Object} - Transformed row with correct field keys
 */
const transformRow = (row, headerMapping) => {
  const transformed = {};
  Object.entries(headerMapping).forEach(([fieldKey, columnName]) => {
    if (columnName && row[columnName] !== undefined) {
      transformed[fieldKey] = row[columnName];
    }
  });
  return transformed;
};

/**
 * Transforms all rows using the header mapping
 * @param {Array} rows - Raw rows from spreadsheet
 * @param {Object} headerMapping - Mapping of field keys to spreadsheet column names
 * @returns {Array} - Transformed rows
 */
export const transformData = (rows, headerMapping) => (
  rows.map((row) => transformRow(row, headerMapping)).filter((row) => Object.keys(row).length > 0)
);

/**
 * Validates that all required fields are present in the row
 * @param {Object} row - Transformed row
 * @param {Array} headers - Header configuration with required flags
 * @returns {Object} - { valid: boolean, missingFields: Array }
 */
const validateRow = (row, headers) => {
  const requiredHeaders = headers.filter((h) => h.required);
  const missingFields = requiredHeaders
    .filter((header) => !row[header.key] && row[header.key] !== 0)
    .map((header) => header.title);
  return {
    valid: missingFields.length === 0,
    missingFields,
  };
};

/**
 * Validates all rows and returns validation results
 * @param {Array} rows - Transformed rows
 * @param {Array} headers - Header configuration
 * @returns {Object} - { validRows: Array, invalidRows: Array }
 */
export const validateData = (rows, headers) => {
  const validRows = [];
  const invalidRows = [];

  rows.forEach((row, index) => {
    const { valid, missingFields } = validateRow(row, headers);
    if (valid) {
      validRows.push({ row, sheetRowNumber: index + 2 }); // +2 for 1-based and header
    } else {
      invalidRows.push({ row, index, missingFields });
    }
  });

  return { validRows, invalidRows };
};

/**
 * Gets the division ID from row data or falls back to first available division
 * @param {Object} row - Row data
 * @param {Object} context - Context with divisionMap and allDivisionIds
 * @returns {number|null} - Division ID
 */
const getDivisionId = (row, context) => {
  if (row.division?.length) {
    return context.divisionMap[row.division];
  }
  if (context.defaultDivisionId) return context.defaultDivisionId;
  return context.allDivisionIds?.[0] || undefined;
};

/**
 * Gets division IDs from row data or falls back to all available divisions
 * @param {Object} row - Row data
 * @param {Object} context - Context with divisionMap and allDivisionIds
 * @returns {Array<number>} - Division IDs
 */
const getDivisionIds = (row, context) => {
  if (row.division?.length) {
    return context.divisionMap[row.division]
      ? [context.divisionMap[row.division]]
      : [];
  }
  if (context.defaultDivisionId) return [context.defaultDivisionId];
  return context.allDivisionIds || [];
};

/**
 * True if transformed item has valid division (divisionId or non-empty divisionIds/divisions).
 * @param {Object} item - Transformed payload from transformFn
 * @returns {boolean}
 */
const hasValidDivision = (item) => {
  if ('divisionId' in item) {
    return !isNullOrUndefined(item.divisionId) && item.divisionId !== '';
  }
  if (item.divisionIds !== undefined) {
    return Array.isArray(item.divisionIds) && item.divisionIds.length > 0;
  }
  // For users import
  if (item.divisions !== undefined) {
    return Array.isArray(item.divisions) && item.divisions.length > 0;
  }
  // For costcodes import
  if (item.costcodes?.[0] && 'divisionId' in item.costcodes[0]) {
    const id = item.costcodes[0].divisionId;
    return id != null && id !== '';
  }
  return true;
};

const parseCoordinate = (value, label) => {
  const errorMessage = `${label} must be a comma separated list of two numbers`;

  if (!value) throw new Error(errorMessage);
  const splitCoordinates = value.split(',');
  if (splitCoordinates.length !== 2) {
    throw new Error(errorMessage);
  }
  const lat = Number.parseFloat(splitCoordinates[0]);
  const lng = Number.parseFloat(splitCoordinates[1]);
  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    throw new Error(errorMessage);
  }
  return { lat, lng };
};

/**
 * Geocodes an address using Google Maps Geocoder
 * @param {string} address - Address string to geocode
 * @returns {Promise<{lat: number, lng: number}>} - Coordinates or { lat: 0, lng: 0 } on failure
 */
const geocodeAddress = (address) => new Promise((resolve) => {
  if (!address || !window.google?.maps?.Geocoder) {
    resolve({ lat: 0, lng: 0 });
    return;
  }
  const geocoder = new window.google.maps.Geocoder();
  try {
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const { location } = results[0].geometry || {};
        resolve({ lat: location?.lat() || 0, lng: location?.lng() || 0 });
      } else {
        resolve({ lat: 0, lng: 0 });
      }
    });
  } catch {
    resolve({ lat: 0, lng: 0 });
  }
});

/**
 * Parse a labels column into API format.
 * Returns array of { labelId } or { title } only
 */
const parseLabels = (labelValue, labelMap) => {
  if (!labelValue || !String(labelValue).trim()) return [];
  const title = String(labelValue).trim(); // only one label for now
  const existing = labelMap?.[title];
  return existing ? [{ labelId: existing.id }] : [{ title }];
};

// API endpoint configurations for each import type.
const IMPORT_API_CONFIG = {
  [IMPORT_TYPES.MATERIALS]: {
    endpoint: '/materials',
    transformFn: (row, context) => {
      const divisionId = getDivisionId(row, context);

      const defaultLocation = context.globalMaterialLocationNameMap?.DEFAULT;

      const payload = {
        name: row.name,
        description: row.description || '',
        partNumber: row.partNumber?.toString() || '',
        divisionIds: divisionId ? [divisionId] : [],
        costcodeId: context.costcodeMap?.[row.costcode]?.id,
        supplier: row.supplier || '',
        cost: parseFloat(row.cost) || 0,
        markup: parseFloat(row.markup) || undefined,
        units: row.units || undefined,
        quantity: parseFloat(row.quantity) || 0,
        quantityThreshold: parseFloat(row.quantityThreshold) || 0,
        labourCost: parseFloat(row.labourCost) || undefined,
        labourMarkup: parseFloat(row.labourMarkup) || undefined,
      };

      if (row.vendor) {
        const vendorName = String(row.vendor).trim();
        const existingVendor = context.vendorMap?.[vendorName];
        if (existingVendor) {
          payload.vendorId = existingVendor.id;
        } else {
          payload.vendor = { name: vendorName };
        }
      }
      if (!isNullOrUndefined(row.quantity) && defaultLocation) {
        payload.createdLocations = [{
          globalMaterialLocationsId: defaultLocation.id,
          quantity: parseFloat(row.quantity) || 0,
          quantityAllocated: 0,
          isDefault: true,
        }];
      }
      return payload;
    },
  },
  [IMPORT_TYPES.COST_CODES]: {
    endpoint: '/costcodes',
    transformFn: (row, context) => {
      const categoryId = context.categoryMap?.[row.category]?.id || 1;
      validateCostCodeConflict(row, categoryId);

      const payload = {
        code: row.code?.toString() || '',
        name: row.name,
        description: row.description || '',
        divisionId: getDivisionId(row, context),
        categoryId,
        categoryIds: [categoryId],
        hourlyWage: parseFloat(row.hourlyWage) || undefined,
        dailyWage: parseFloat(row.dailyWage) || undefined,
        wageAdjustment: parseFloat(row.wageAdjustment) || undefined,
        wageMultiplier: parseFloat(row.wageMultiplier) || undefined,
        hourlyBillingRate: parseFloat(row.hourlyBillingRate) || undefined,
        dailyBillingRate: parseFloat(row.dailyBillingRate) || undefined,
      };

      applyCustomFieldsForPayload(row, context.costcodeCustomFields?.sections, context, payload);

      return {
        costcodes: [payload],
      };
    },
  },
  [IMPORT_TYPES.PROJECTS]: {
    endpoint: '/projects',
    shouldGeocode: true,
    transformFn: (row, context) => {
      const existingCustomer = context.customerMap?.[row.customer];

      const useCoordinates = !!context.useProjectCoordinates;

      let lat = 0;
      let lng = 0;
      let address = row.address?.toString().trim() ?? '';
      if (useCoordinates) {
        const coordinates = parseCoordinate(address, 'Address');
        lat = coordinates.lat;
        lng = coordinates.lng;
        address = `${lat}, ${lng}`;
      }

      const payload = {
        name: row.name,
        number: row.number?.toString() || undefined,
        address,
        useCoordinates,
        divisionId: getDivisionId(row, context),
        projectTypeId: context.projectTypeMap?.[row.projectType]?.id,
        info: row.info || '',
        startDate: row.startDate || undefined,
        endDate: row.endDate || undefined,
        geofence: parseFloat(row.geofence) || undefined,
        contractValue: parseFloat(row.contractValue) || undefined,
        estimatedLabourCost: parseFloat(row.estimatedLabourCost) || undefined,
        estimatedLabourHours: parseFloat(row.estimatedLabourHours) || undefined,
        estimatedMaterialCost: parseFloat(row.estimatedMaterialCost) || undefined,
        estimatedOverheadCost: parseFloat(row.estimatedOverheadCost) || undefined,
        estimatedEquipmentCost: parseFloat(row.estimatedEquipmentCost) || undefined,
        lat,
        lng,
        addedLabels: parseLabels(row.labels, context.labelMap),
      };

      if (row.customer) {
        if (existingCustomer) {
          payload.customerId = existingCustomer.id;
        } else {
          payload.customer = { name: row.customer };
        }
      }

      applyCustomFieldsForPayload(row, context.projectCustomFields?.sections, context, payload);

      return payload;
    },
  },
  [IMPORT_TYPES.CUSTOMERS]: {
    endpoint: '/customers',
    transformFn: (row, context) => {
      const payload = {
        name: row.name,
        officeEmail: row.officeEmail || undefined,
        billingEmail: row.billingEmail || undefined,
        officeNumber: row.officeNumber || undefined,
        mobileNumber: row.mobileNumber || undefined,
        faxNumber: row.faxNumber || undefined,
        officeAddress: row.officeAddress || undefined,
        billingAddress: row.billingAddress || undefined,
        website: row.website || undefined,
        info: row.info || undefined,
        materialDiscount: parseFloat(row.materialDiscount) || undefined,
        divisionIds: getDivisionIds(row, context),
      };

      applyCustomFieldsForPayload(row, context.customerCustomFields?.sections, context, payload);

      return payload;
    },
  },
  [IMPORT_TYPES.VENDORS]: {
    endpoint: '/vendors',
    transformFn: (row, context) => ({
      name: row.name,
      officeEmail: row.officeEmail || undefined,
      billingEmail: row.billingEmail || undefined,
      officeNumber: row.officeNumber || undefined,
      mobileNumber: row.mobileNumber || undefined,
      faxNumber: row.faxNumber || undefined,
      officeAddress: row.officeAddress || undefined,
      billingAddress: row.billingAddress || undefined,
      website: row.website || undefined,
      info: row.info || undefined,
      type: row.type || undefined,
      divisionIds: getDivisionIds(row, context),
    }),
  },
  [IMPORT_TYPES.EQUIPMENT]: {
    endpoint: '/equipment',
    transformFn: (row, context) => {
      const divisionIds = getDivisionIds(row, context);

      const payload = {
        name: row.name,
        divisionIds,
        statusId: context.statusMap?.[row.status]?.id || context.defaultStatusId,
        code: row.code ? String(row.code).trim() : null,
        equipmentTypeId: context.equipmentTypeMap?.[row.equipmentType]?.id,
        costcodeId: context.costcodeMap?.[row.costcode]?.id,
        hourlyCost: parseFloat(row.hourlyCost) || undefined,
        hourlyBillingRate: parseFloat(row.hourlyBillingRate) || undefined,
        dailyCost: parseFloat(row.dailyCost) || undefined,
        dailyBillingRate: parseFloat(row.dailyBillingRate) || undefined,
        weeklyCost: parseFloat(row.weeklyCost) || undefined,
        weeklyBillingRate: parseFloat(row.weeklyBillingRate) || undefined,
        monthlyCost: parseFloat(row.monthlyCost) || undefined,
        monthlyBillingRate: parseFloat(row.monthlyBillingRate) || undefined,
        labels: parseLabels(row.labels, context.labelMap),
      };

      if (row.vendor) {
        const vendorName = String(row.vendor).trim();
        const existingVendor = context.vendorMap?.[vendorName];
        if (existingVendor) {
          payload.vendorId = existingVendor.id;
        } else {
          payload.vendor = { name: vendorName };
        }
      }

      return payload;
    },
  },
  [IMPORT_TYPES.USERS]: {
    endpoint: '/users',
    transformFn: (row, context) => {
      const payload = {
        name: row.name,
        username: row.username || deriveUsername(row.name),
        email: row.email,
        password: row.password || generateRandomString(true),
        position: row.position,
        divisions: getDivisionIds(row, context),
        phoneNumber: row.phoneNumber,
        employeeId: row.employeeId,
        wage: isNullOrUndefined(row.wage) ? undefined : parseFloat(row.wage),
        timezone: row.timezone || context.timezone,
        address: row.address || undefined,
        sendWelcomeEmail: context.sendWelcomeEmail ?? true,
      };

      applyCustomFieldsForPayload(row, context.userCustomFields?.sections, context, payload);

      return payload;
    },
  },
  [IMPORT_TYPES.UNIONS]: {
    endpoint: '/unions/import',
    skipDivisionCheck: true,
    transformFn: (row) => ({
      union: row.union,
      local: row.local || undefined,
      localDescription: row.localDescription || undefined,
      class: row.class || undefined,
      classDescription: row.classDescription || undefined,
      classWage: row.classWage ? Number.parseFloat(row.classWage) : null,
    }),
  },
  [IMPORT_TYPES.BUCKETS]: {
    endpoint: '/buckets',
    transformFn: (row, context) => {
      const bucketTemplate = context.selectedBucketTemplate;
      if (!bucketTemplate) {
        throw new Error('Bucket template is required. Please select a bucket template in the header mapping step.');
      }

      const payload = {
        name: row.name,
        description: row.description || undefined,
        bucketTemplateId: bucketTemplate.id,
      };

      if (row.status) {
        const status = context.bucketStatusMap?.[row.status];
        if (status) {
          payload.statusId = status.id;
        } else {
          const errorMessage = `Status "${row.status}" not found in bucket template "${bucketTemplate.name}"`;
          throw new Error(errorMessage);
        }
      }

      const customerErr = applyBucketImportCustomerLink(row, context, payload);
      if (customerErr) {
        throw new Error(customerErr);
      }

      const projectErr = applyBucketImportProjectLink(row, context, payload);
      if (projectErr) {
        throw new Error(projectErr);
      }

      applyCustomFieldsForPayload(row, bucketTemplate.customFields?.sections, context, payload);

      return payload;
    },
  },
  [IMPORT_TYPES.BULK_USER_UPDATE]: {
    endpoint: '/users',
    isUpdate: true,
    skipDivisionCheck: true,
    transformFn: (row, context) => buildUpdateUserImportPayload(row, context),
  },
  [IMPORT_TYPES.BULK_MATERIAL_UPDATE]: {
    endpoint: '/materials',
    isUpdate: true,
    skipDivisionCheck: true,
    transformFn: (row, context) => buildUpdateMaterialImportPayload(row, context),
  },
  [IMPORT_TYPES.BULK_EQUIPMENT_UPDATE]: {
    endpoint: '/equipment',
    isUpdate: true,
    skipDivisionCheck: true,
    transformFn: (row, context) => buildUpdateEquipmentImportPayload(row, context),
  },
};

/**
 * Extracts error message from API response, including Joi validation errors
 * @param {*} error - Error from API response
 * @returns {string} - Error message
 */
const getErrorMessage = (error) => {
  // Handle Joi validation errors with details array
  if (typeof error === 'object' && error.validation && Array.isArray(error.validation.details)) {
    // Format Joi validation errors: "Field 'name' is required; Field 'email' must be a valid email"
    const validationErrors = error.validation.details.map((detail) => (
      `${detail.message.replaceAll(/"/g, "'")}`
    )).join('; ');
    return validationErrors || error.message || 'Validation error';
  }

  // Handle error objects with details array (Joi format without validation wrapper)
  if (typeof error === 'object' && Array.isArray(error.details)) {
    const validationErrors = error.details.map((detail) => (
      `${detail.message.replaceAll(/"/g, "'")}`
    )).join('; ');
    return validationErrors || 'Validation error';
  }

  // Handle standard error objects with message property
  if (typeof error === 'object' && error.message) {
    return error.message;
  }

  // Fallback to string representation
  return String(error) || 'Unknown error';
};

/**
 * Gets the created entity id from a success item's response.
 * Cost codes POST returns an array of one costcode; other resources return the entity object.
 * Customers and vendors return { customer/vendor: {...}, labels: [] } structure.
 * @param {Object} successItem - { response } from processImport success array
 * @returns {string|undefined} - Created entity id
 */
export const getIdFromSuccessItem = (successItem) => {
  const r = successItem?.response;
  if (!r) return undefined;
  if (Array.isArray(r)) return r[0]?.id;
  if (r.customer?.id) return r.customer.id;
  if (r.vendor?.id) return r.vendor.id;
  if (r.create?.id) return r.create.id;
  return r.id;
};

/**
 * Deletes a single resource by id
 * @param {string} endpoint - Base path e.g. '/materials', '/costcodes'
 * @param {string} id - Entity id
 * @param {Object} query - Query parameters
 * @returns {Promise<{ err?: string }>}
 */
const deleteSingle = async (endpoint, id, query = {}) => {
  try {
    await axios.delete(`${endpoint}/${id}`, { params: query });
    return { err: null };
  } catch (error) {
    // Extract the full error object to preserve validation details
    const rawError = error.response?.data
      || error.message
      || 'Unknown error';
    return { err: getErrorMessage(rawError) };
  }
};

/**
 * Rollback a successful import by deleting all created entities.
 * Creates one entity per row; rollback deletes each created entity.
 * @param {string} importType - Type of import
 * @param {Array} success - Success array from executeImport
 * @returns {Promise<{ rolledBack: number, failed: Array<{ id, error }> }>}
 */
export const rollbackImport = async (importType, success) => {
  const config = IMPORT_API_CONFIG[importType];
  if (!config) {
    throw new Error(`Unknown import type: ${importType}`);
  }
  const { endpoint } = config;
  const result = { rolledBack: 0, failed: [] };
  const shouldDeleteUnarchived = importType === IMPORT_TYPES.PROJECTS;
  for (let i = 0; i < success.length; i += 1) {
    const id = getIdFromSuccessItem(success[i]);
    if (id) {
      // eslint-disable-next-line no-await-in-loop -- sequential rollback
      const response = await deleteSingle(
        endpoint,
        id,
        shouldDeleteUnarchived ? { deleteUnarchived: true } : {},
      );
      if (response.err) {
        result.failed.push({ id, error: response.err });
      } else {
        result.rolledBack += 1;
      }
    }
  }
  return result;
};

/**
 * Creates a single item via API
 * @param {string} endpoint - API endpoint
 * @param {Object} payload - Item payload
 * @returns {Promise} - API response with error details
 */
const createSingle = async (endpoint, payload) => {
  try {
    const response = await axios.post(endpoint, payload);
    return { data: response.data, err: null };
  } catch (error) {
    // Extract the full error object to preserve Joi validation details
    const rawError = error.response?.data
      || error.message
      || 'Unknown error';
    return { data: null, err: getErrorMessage(rawError) };
  }
};

const updateSingle = async (endpoint, id, payload) => {
  if (!id) {
    return { data: null, err: 'Update ID is required' };
  }
  try {
    const response = await axios.put(`${endpoint}/${id}`, payload);
    return { data: response.data, err: null };
  } catch (error) {
    const rawError = error.response?.data
      || error.message
      || 'Unknown error';
    return { data: null, err: getErrorMessage(rawError) };
  }
};

const geocodeIfNeeded = async (shouldGeocode, apiPayload) => {
  if (shouldGeocode && apiPayload.address && !apiPayload.useCoordinates) {
    const coords = await geocodeAddress(apiPayload.address);
    apiPayload.lat = coords.lat;
    apiPayload.lng = coords.lng;
  }
};

/**
 * Processes the import for a given type
 * @param {string} importType - Type of import (from IMPORT_TYPES)
 * @param {Array} rows - Transformed and validated rows
 * @param {Object} context - Context with lookup maps (divisionMap, etc.)
 * @param {Function} onProgress - Callback for progress updates ({ percent, current, total })
 * @returns {Promise<Object>} - { success: Array, failed: Array }
 */
export const processImport = async (importType, rows, context = {}, onProgress = null) => {
  const config = IMPORT_API_CONFIG[importType];
  if (!config) {
    throw new Error(`Unknown import type: ${importType}`);
  }

  const {
    endpoint,
    transformFn,
    shouldGeocode,
    isUpdate,
    skipDivisionCheck,
  } = config;

  const results = {
    success: [],
    failed: [],
  };

  const total = rows.length;

  for (let i = 0; i < rows.length; i += 1) {
    const { row: originalRow, sheetRowNumber } = rows[i];
    let item;
    try {
      // Track row index for skipped fields
      context.currentRowIndex = i;
      item = transformFn(originalRow, context);
    } catch (transformError) {
      results.failed.push({
        rowNumber: sheetRowNumber,
        originalRow,
        transformedItem: null,
        error: transformError.message || String(transformError),
      });
      // Report progress
      if (onProgress) {
        const percent = Math.round(((i + 1) / total) * 100);
        onProgress({ percent, current: i + 1, total });
      }
      // eslint-disable-next-line no-continue
      continue;
    }

    const isDivisionValid = skipDivisionCheck || hasValidDivision(item);

    if (isDivisionValid) {
      const apiPayload = isUpdate ? item.payload : item;
      const updateId = isUpdate ? item.updateId : undefined;

      /* eslint-disable no-await-in-loop -- sequential per-row import */
      // Geocode address if configured and address is present
      await geocodeIfNeeded(shouldGeocode, apiPayload);

      let response;
      if (isUpdate) {
        response = await updateSingle(endpoint, updateId, apiPayload);
      } else {
        response = await createSingle(endpoint, apiPayload);
      }
      /* eslint-enable no-await-in-loop */

      if (response.err) {
        results.failed.push({
          rowNumber: sheetRowNumber,
          originalRow,
          transformedItem: item,
          error: response.err,
        });
      } else if (response.data) {
        results.success.push({
          index: i,
          item,
          response: response.data,
          displayRow: originalRow,
          rowNumber: sheetRowNumber,
        });

        // If a customer was inline-created, update the customerMap so
        // subsequent rows with the same customer name reuse the ID
        const responseData = response.data;
        if (apiPayload.customer?.name && responseData?.customerId && context.customerMap) {
          context.customerMap[apiPayload.customer.name] = { id: responseData.customerId };
        }
      } else {
        results.failed.push({
          rowNumber: sheetRowNumber,
          originalRow,
          transformedItem: item,
          error: 'API returned no response',
        });
      }
    } else {
      results.failed.push({
        rowNumber: sheetRowNumber,
        originalRow,
        transformedItem: item,
        error: 'Invalid Division',
      });
    }

    // Report progress
    if (onProgress) {
      const percent = Math.round(((i + 1) / total) * 100);
      onProgress({ percent, current: i + 1, total });
    }
  }

  return results;
};

/**
 * Main import function that orchestrates the entire import process
 * @param {string} importType - Type of import
 * @param {Array} rawRows - Raw rows from spreadsheet
 * @param {Object} headerMapping - Header mapping
 * @param {Array} headers - Header configuration
 * @param {Object} context - Context with lookup maps
 * @param {Function} onProgress - Callback for progress updates ({ percent, current, total })
 * @returns {Promise<Object>} - Import results
 */
export const executeImport = async (
  importType,
  rawRows,
  headerMapping,
  headers,
  context = {},
  onProgress = null,
) => {
  // Transform the data using header mapping
  const transformedRows = transformData(rawRows, headerMapping);

  // Validate the data
  const { validRows, invalidRows } = validateData(transformedRows, headers);

  // Format validation errors
  const validationErrors = invalidRows.map((r) => ({
    rowNumber: r.index + 2, // +2 for 1-based index and header row
    originalRow: r.row,
    error: `Missing required fields: ${r.missingFields.join(', ')}`,
  }));

  if (validRows.length === 0) {
    return {
      success: [],
      failed: validationErrors,
      skipped: [],
    };
  }

  // Process the import
  const { success, failed } = await processImport(importType, validRows, context, onProgress);

  return {
    success,
    failed: [...failed, ...validationErrors],
    skipped: [],
  };
};

export default {
  transformData,
  validateData,
  processImport,
  executeImport,
  getCustomFieldHeaders,
  getBoardIdsFromCustomFieldSections,
};
